import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Calendar, CreditCard, Check, X, Crown, RefreshCw, ChevronRight, ChevronDown, Loader2, QrCode, FileText, Copy, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { DiaVencimento } from "../features/assinatura-dia";
import { CarregandoBloco, CarregandoLista } from "../components/carregando";
import { PerguntaAntesDePagar, QuemIndicou, useIndicacao } from "../features/assinatura-indicacao";

const API = "/api";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const fmtBRL = (n) =>
  (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Renderiza um valor monetário. Se for 0 e a flag `courtesy` estiver ligada (ou amount==0
// em uma fatura), exibe um badge "Grátis" verde no lugar do R$ 0,00.
const FreeBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
    GRÁTIS
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso.length <= 10 ? iso + "T00:00:00" : iso).toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
};

const STATUS_STYLE = {
  paid: { label: "Pago", className: "bg-green-500/15 text-green-600 dark:text-green-400" },
  open: { label: "Em aberto", className: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  overdue: { label: "Vencida", className: "bg-red-500/15 text-red-600 dark:text-red-400" },
  cancelled: { label: "Cancelada", className: "bg-red-500/15 text-red-600 dark:text-red-400" },
};

export default function Assinatura() {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [changing, setChanging] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    try {
      const u = localStorage.getItem("user");
      if (u) setMe(JSON.parse(u));
    } catch (_) {}
  }, []);

  const isAdmin = useMemo(() => {
    const r = (me?.role || "").toLowerCase();
    return r === "proprietario" || r === "administrador";
  }, [me]);

  // `loading` só vale na 1ª abertura (esqueleto). "Atualizar" e a troca de plano
  // recarregam por trás, só girando o ícone do botão (25/10/2026).
  const [atualizando, setAtualizando] = useState(false);
  const fetchAll = async () => {
    setAtualizando(true);
    try {
      const [curRes, plansRes, invRes] = await Promise.all([
        axios.get(`${API}/billing/current`, getAuthHeader()),
        axios.get(`${API}/billing/plans`, getAuthHeader()),
        axios.get(`${API}/billing/invoices`, getAuthHeader()),
      ]);
      setCurrent(curRes.data || null);
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar dados da assinatura");
    } finally {
      setLoading(false);
      setAtualizando(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleChangePlan = async (slug) => {
    if (!isAdmin) {
      toast.error("Apenas o proprietário ou administrador pode mudar o plano");
      return;
    }
    if (current?.plan_slug === slug) return;
    if (!window.confirm(`Confirma a mudança para o plano "${slug}"?`)) return;
    setChanging(slug);
    try {
      await axios.post(`${API}/billing/change-plan`, { plan_slug: slug }, getAuthHeader());
      toast.success("Plano atualizado");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao mudar de plano");
    } finally {
      setChanging(null);
    }
  };

  // ===== pagamento da fatura (PIX / boleto na conta do Nucleo) =====
  const [payInvoice, setPayInvoice] = useState(null);   // fatura selecionada
  const [payMethod, setPayMethod] = useState(null);     // 'pix' | 'boleto'
  const [payData, setPayData] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [filtro, setFiltro] = useState("todas");        // todas | abertas | pagas
  // Indique e Ganhe (23/10/2026): antes de pagar a 1ª fatura a tela pergunta se teve indicação.
  const indicacao = useIndicacao();
  const [pagamentoPendente, setPagamentoPendente] = useState(null);

  const pagar = async (inv, metodo, renovar = false) => {
    setPayInvoice(inv);
    setPayMethod(metodo);
    setPayError("");
    setCopied(false);
    if (!renovar) setPayData(null);
    setPayLoading(true);
    try {
      const r = await axios.post(
        `${API}/billing/invoices/${inv.id}/${metodo}${renovar ? "?renovar=true" : ""}`,
        {},
        getAuthHeader()
      );
      setPayData(r.data);
    } catch (e) {
      setPayError(e.response?.data?.detail || "Não foi possível gerar o pagamento");
    } finally {
      setPayLoading(false);
    }
  };

  const pagarOuPerguntar = (inv, metodo) => {
    if (indicacao.precisaPerguntar && isAdmin) { setPagamentoPendente({ inv, metodo }); return; }
    pagar(inv, metodo);
  };

  const copiarCodigo = async () => {
    const texto = payData?.copia_cola || payData?.linha_digitavel;
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
    } catch (_) {
      const el = document.getElementById("codigoPagamento");
      if (el) { el.select(); document.execCommand("copy"); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // fatura escolhida na lista sobe pro destaque
  const selecionarFatura = (inv) => {
    setPayInvoice(inv);
    setPayMethod(null);
    setPayData(null);
    setPayError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cabecalho = (
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-7 h-7 text-primary" />
            Assinatura
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Suas faturas do Núcleo — pague por PIX ou boleto sem sair daqui
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={atualizando}>
          <RefreshCw className={`w-4 h-4 mr-1.5 ${atualizando ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>
  );

  // Carregamento com cards (25/10/2026): plano, fatura e histórico piscando no
  // lugar do spinner — sem "Plano —" ou "Você está em dia" antes da hora.
  if (loading) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {cabecalho}
        <CarregandoBloco altura="h-[74px]" />
        <CarregandoBloco altura="h-28" />
        <CarregandoLista linhas={3} colunas={1} />
      </div>
    );
  }

  const plan = current?.plan;
  const planSlug = current?.plan_slug;
  const cycleLabel = (current?.billing_cycle || "monthly") === "monthly" ? "Mensal" : "Anual";
  const last4 = current?.payment_method_last4;
  const cardBrand = current?.payment_method_brand;
  const totalInvoices = invoices.length;

  // faturas em aberto, da mais antiga pra mais nova
  const abertas = invoices
    .filter((i) => i.status === "open" || i.status === "overdue")
    .sort((x, y) => String(x.due_date).localeCompare(String(y.due_date)));
  const hero = (payInvoice && abertas.find((i) => i.id === payInvoice.id)) || abertas[0] || null;
  const hoje = new Date().toISOString().slice(0, 10);
  const vencida = hero && hero.due_date && String(hero.due_date).slice(0, 10) < hoje;
  const diasParaVencer = hero?.due_date
    ? Math.round((new Date(String(hero.due_date).slice(0, 10) + "T12:00:00") - new Date(hoje + "T12:00:00")) / 86400000)
    : null;

  const filtradas = invoices.filter((i) =>
    filtro === "abertas" ? (i.status === "open" || i.status === "overdue")
      : filtro === "pagas" ? i.status === "paid"
        : true);

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      {cabecalho}

      {/* Plano — uma linha, sem roubar a cena da fatura */}
      <div className="bg-card border rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shrink-0">
          <Crown className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="font-extrabold text-orange-500 leading-tight">
            Plano {plan?.name || planSlug || "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {current?.is_courtesy ? "Cortesia" : `${fmtBRL(current?.effective_amount ?? plan?.price ?? 0)}/mês`}
            {current?.subscribed_at ? ` · assinante desde ${fmtDate(current.subscribed_at)}` : ""}
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[11px] text-muted-foreground uppercase font-medium">Próxima cobrança</p>
          <p className="text-sm font-semibold flex items-center gap-1 justify-end">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            {current?.next_invoice_at ? fmtDate(current.next_invoice_at) : "—"}
          </p>
        </div>
      </div>

      {/* Dia de vencimento: o lojista escolhe; a troca vale do mês seguinte em diante */}
      <DiaVencimento podeAlterar={isAdmin} />

      {/* Indique e Ganhe: quem indicou esta loja (até a 1ª fatura paga) */}
      <QuemIndicou indicacao={indicacao} podeAlterar={isAdmin} />

      {/* ==================== FATURA EM ABERTO (destaque) ==================== */}
      {hero ? (
        <div className={`rounded-2xl border-2 overflow-hidden ${vencida ? "border-red-500/60" : "border-amber-500/60"}`}>
          <div className={`px-5 py-2.5 flex items-center gap-2 text-sm font-bold ${vencida ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`}>
            <AlertTriangle className="w-4 h-4" />
            {vencida
              ? `Fatura vencida há ${Math.abs(diasParaVencer)} dia${Math.abs(diasParaVencer) === 1 ? "" : "s"}`
              : abertas.length > 1
                ? `Você tem ${abertas.length} faturas em aberto`
                : "Fatura em aberto"}
          </div>

          <div className="bg-card p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">
                  {hero.descricao || "Mensalidade"}
                  {hero.period_start ? ` · cobre ${fmtDate(hero.period_start)} a ${fmtDate(hero.period_end)}` : ""}
                </p>
                <p className="text-3xl font-extrabold tabular-nums mt-0.5">{fmtBRL(hero.amount_due ?? hero.amount)}</p>
                {hero.encargos?.dias_atraso > 0 && (
                  <p className="text-[11px] text-red-500 mt-0.5">
                    {fmtBRL(hero.encargos.valor_base)} + {fmtBRL(hero.encargos.multa)} multa (2%) + {fmtBRL(hero.encargos.juros)} juros (1% a.m. · {hero.encargos.dias_atraso} dia{hero.encargos.dias_atraso === 1 ? "" : "s"})
                  </p>
                )}
                <p className={`text-xs mt-1 ${vencida ? "text-red-500 font-semibold" : "text-muted-foreground"}`}>
                  {vencida
                    ? `venceu em ${fmtDate(hero.due_date)}`
                    : diasParaVencer === 0
                      ? "vence hoje"
                      : `vence em ${fmtDate(hero.due_date)} (${diasParaVencer} dia${diasParaVencer === 1 ? "" : "s"})`}
                </p>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => pagarOuPerguntar(hero, "pix")}
                  disabled={payLoading}
                  className={payMethod === "pix" && payInvoice?.id === hero.id ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                  variant={payMethod === "pix" && payInvoice?.id === hero.id ? "secondary" : "default"}
                >
                  <QrCode className="w-4 h-4 mr-1.5" /> Pagar com PIX
                </Button>
                <Button
                  variant="outline"
                  disabled={payLoading}
                  onClick={() => pagarOuPerguntar(hero, "boleto")}
                >
                  <FileText className="w-4 h-4 mr-1.5" /> Boleto
                </Button>
              </div>
            </div>

            {/* painel de pagamento inline */}
            {payInvoice?.id === hero.id && (payLoading || payError || payData) && (
              <div className="mt-5 pt-5 border-t">
                {payLoading && (
                  <div className="flex items-center gap-3 py-6 justify-center text-sm text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    {payMethod === "boleto" ? "Emitindo boleto..." : "Gerando PIX..."}
                  </div>
                )}

                {!payLoading && payError && (
                  <div className="py-4 text-center">
                    <p className="text-sm text-red-500 font-medium">{payError}</p>
                    <Button size="sm" variant="outline" className="mt-3"
                      onClick={() => pagar(hero, payMethod, true)}>
                      Tentar de novo
                    </Button>
                  </div>
                )}

                {!payLoading && !payError && payData && payMethod === "pix" && (
                  <div className="flex flex-col md:flex-row gap-5 items-center">
                    {payData.qr_base64 && (
                      <img
                        src={`data:image/png;base64,${payData.qr_base64}`}
                        alt="QR Code PIX"
                        className="w-44 h-44 rounded-xl bg-white p-1.5 shrink-0"
                      />
                    )}
                    <div className="flex-1 w-full min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                        PIX copia e cola
                      </p>
                      <textarea
                        id="codigoPagamento"
                        readOnly
                        rows={3}
                        value={payData.copia_cola}
                        onFocus={(e) => e.target.select()}
                        className="w-full text-[10px] font-mono p-2.5 rounded-lg bg-muted/50 border resize-none break-all"
                      />
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Button size="sm" onClick={copiarCodigo}
                          className={copied ? "bg-emerald-600 hover:bg-emerald-600" : ""}>
                          {copied
                            ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Copiado!</>
                            : <><Copy className="w-4 h-4 mr-1.5" /> Copiar código</>}
                        </Button>
                        <button type="button" onClick={() => pagar(hero, "pix", true)}
                          className="text-xs font-semibold text-primary hover:underline">
                          Gerar novo
                        </button>
                        <span className="text-[11px] text-muted-foreground ml-auto">
                          {payData.expira_em ? `vale até ${fmtDate(payData.expira_em)}` : ""}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Assim que o pagamento cair, a baixa é feita pela equipe do Núcleo.
                      </p>
                    </div>
                  </div>
                )}

                {!payLoading && !payError && payData && payMethod === "boleto" && (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                      Linha digitável
                    </p>
                    <textarea
                      id="codigoPagamento"
                      readOnly
                      rows={2}
                      value={payData.linha_digitavel}
                      onFocus={(e) => e.target.select()}
                      className="w-full text-xs font-mono p-2.5 rounded-lg bg-muted/50 border resize-none break-all"
                    />
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Button size="sm" onClick={copiarCodigo}
                        className={copied ? "bg-emerald-600 hover:bg-emerald-600" : ""}>
                        {copied
                          ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Copiado!</>
                          : <><Copy className="w-4 h-4 mr-1.5" /> Copiar linha digitável</>}
                      </Button>
                      {payData.boleto_url && (
                        <a href={payData.boleto_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                          <ExternalLink className="w-3.5 h-3.5" /> Abrir boleto (PDF)
                        </a>
                      )}
                      <span className="text-[11px] text-muted-foreground ml-auto">
                        {payData.expira_em ? `vence ${fmtDate(payData.expira_em)}` : ""}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 px-5 py-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <p className="font-bold text-emerald-600 dark:text-emerald-400">Você está em dia 🎉</p>
            <p className="text-sm text-muted-foreground">
              Nenhuma fatura em aberto.
              {current?.next_invoice_at ? ` A próxima cobrança chega em ${fmtDate(current.next_invoice_at)}.` : ""}
            </p>
          </div>
        </div>
      )}

      {/* ==================== HISTÓRICO ==================== */}
      <div className="bg-card border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold">Histórico</h2>
            <p className="text-xs text-muted-foreground">
              {totalInvoices} transaç{totalInvoices === 1 ? "ão" : "ões"} desde o início da assinatura
            </p>
          </div>
          <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-1">
            {[["todas", "Todas"], ["abertas", "Em aberto"], ["pagas", "Pagas"]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setFiltro(v)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  filtro === v ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
                {v === "abertas" && abertas.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-[10px] tabular-nums">
                    {abertas.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {filtradas.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Nenhuma transação {filtro === "todas" ? "registrada" : filtro === "abertas" ? "em aberto" : "paga"}.
          </div>
        ) : (
          <div className="divide-y">
            {filtradas.map((inv) => {
              const st = STATUS_STYLE[inv.status] || STATUS_STYLE.open;
              const isCancelled = inv.status === "cancelled";
              const isOpen = inv.status === "open" || inv.status === "overdue";
              const isFree = (Number(inv.amount) || 0) === 0 && !isCancelled;
              return (
                <div
                  key={inv.id}
                  className={`px-5 py-3.5 flex items-center gap-4 ${isCancelled ? "opacity-50" : ""} ${isOpen ? "bg-amber-500/[0.04]" : ""}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    isOpen ? "bg-amber-500/15" : isCancelled ? "bg-red-500/10" : isFree ? "bg-emerald-500/15" : "bg-green-500/15"
                  }`}>
                    {isOpen ? <AlertTriangle className="w-4 h-4 text-amber-500" />
                      : isCancelled ? <X className="w-4 h-4 text-red-500" />
                        : <Check className="w-4 h-4 text-green-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isCancelled ? "line-through decoration-red-500/70" : ""}`}>
                      {inv.descricao || "Mensalidade"}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      cobre {fmtDate(inv.period_start || inv.issue_date)} → {fmtDate(inv.period_end || inv.due_date)}
                      {isOpen ? ` · vence ${fmtDate(inv.due_date)}` : ""}
                      {inv.payment_method_brand && !isFree ? ` · ${inv.payment_method_brand}` : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold tabular-nums ${isCancelled ? "line-through decoration-red-500/70 text-red-500" : ""}`}>
                      {isFree ? <FreeBadge /> : fmtBRL(isOpen ? (inv.amount_due ?? inv.amount) : inv.amount)}
                    </p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${st.className}`}>
                      {st.label}
                    </span>
                  </div>
                  {isOpen && (
                    <Button size="sm" variant="outline" className="shrink-0"
                      onClick={() => selecionarFatura(inv)}>
                      Pagar
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* toggle dos planos */}
      <button
        type="button"
        onClick={() => setShowPlans((v) => !v)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed text-sm font-semibold text-muted-foreground hover:text-foreground hover:border-solid transition-all"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${showPlans ? "rotate-180" : ""}`} />
        {showPlans ? "Ocultar planos" : "Ver planos disponíveis"}
      </button>

      {/* Planos disponíveis — colapsados: hoje só um plano é vendido */}
      {showPlans && (
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold">Planos disponíveis</h2>
          <p className="text-xs text-muted-foreground">
            Compare os planos e escolha o que faz mais sentido para sua loja
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          {plans.map((p) => {
            const isCurrent = planSlug === p.slug;
            const highlight = (p.highlight || "").toLowerCase();
            const isMaisVendido = highlight === "mais vendido";
            // eslint-disable-next-line no-unused-vars
            const isMaisEscolhido = highlight === "mais escolhido";
            // Mostra sempre o preço de lista (sem promo).
            const priceShown = p.price;
            const ctaLabel = p.cta_label || "Contratar";
            // Estilo do card por destaque
            const cardClass = isCurrent
              ? "border-primary shadow-lg ring-2 ring-primary/10"
              : isMaisVendido
                ? "border-orange-500 shadow-[0_0_0_1px_rgba(249,115,22,0.3),0_8px_24px_-12px_rgba(249,115,22,0.5)]"
                : "border-border hover:border-muted-foreground/40";
            return (
              <div
                key={p.slug}
                className={`bg-card border-2 rounded-2xl flex flex-col transition-all relative overflow-hidden ${cardClass}`}
              >
                {isMaisVendido && (
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-center py-2 text-xs font-bold flex items-center justify-center gap-1">
                    🔥 Mais vendido
                  </div>
                )}
                {isCurrent && !isMaisVendido && (
                  <div className="bg-primary text-primary-foreground text-center py-2 text-xs font-bold">
                    Plano contratado
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  <h3 className={`text-xl font-extrabold ${isMaisVendido ? 'text-foreground' : ''}`}>
                    {p.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">{p.tagline}</p>

                  {/* Bloco de preço (sem desconto/promo) */}
                  <div className="mt-4">
                    {(() => {
                      const [reais, cents] = priceShown.toFixed(2).split(".");
                      return (
                        <div className="flex items-baseline gap-1">
                          <span className="text-sm text-muted-foreground">R$</span>
                          <span className={`text-5xl font-black leading-none ${isMaisVendido ? 'text-orange-500' : 'text-foreground'}`}>
                            {reais}
                          </span>
                          <span className={`text-xl font-bold leading-none ${isMaisVendido ? 'text-orange-500' : 'text-foreground'}`}>
                            ,{cents}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">/mês</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Features */}
                  <div className="border-t border-border/60 mt-4 pt-4">
                    <ul className="space-y-2 text-sm">
                      {(p.features || []).map((f, i) => {
                        const isHeader = /^Tudo do/i.test(f);
                        return (
                          <li key={`inc-${i}`} className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-orange-500/15 dark:bg-orange-500/20 flex items-center justify-center shrink-0 mt-0.5">
                              <Check className="w-3 h-3 text-orange-500" strokeWidth={3} />
                            </span>
                            <span className={isHeader ? "font-bold text-orange-600 dark:text-orange-400" : "text-foreground/90"}>
                              {f}
                            </span>
                          </li>
                        );
                      })}
                      {(p.features_excluded || []).map((f, i) => (
                        <li key={`exc-${i}`} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                            <X className="w-3 h-3 text-muted-foreground" strokeWidth={2.5} />
                          </span>
                          <span className="text-muted-foreground/70 line-through">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Módulos como chips */}
                  {Array.isArray(p.modules) && p.modules.length > 0 && (
                    <div className="border-t border-border/60 mt-4 pt-4">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Módulos</p>
                      <div className="flex flex-wrap gap-1.5">
                        {p.modules.map((m, i) => (
                          <span
                            key={i}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md ${
                              m.included
                                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                : 'bg-muted/60 text-muted-foreground'
                            }`}
                          >
                            {m.included ? <Check className="w-3 h-3" strokeWidth={3} /> : null}
                            {m.name}{m.included ? '' : ' +'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="mt-auto pt-4">
                    <Button
                      className={`w-full font-bold ${
                        isMaisVendido && !isCurrent
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30'
                          : ''
                      }`}
                      variant={isCurrent ? "outline" : (isMaisVendido ? "default" : "default")}
                      disabled={isCurrent || changing === p.slug || !isAdmin}
                      onClick={() => handleChangePlan(p.slug)}
                    >
                      {changing === p.slug ? (
                        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Trocando...</>
                      ) : isCurrent ? (
                        "Plano contratado"
                      ) : (
                        <>{isMaisVendido && <Crown className="w-4 h-4 mr-1.5" />}{ctaLabel}<ChevronRight className="w-4 h-4 ml-1" /></>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!isAdmin && (
          <p className="text-xs text-muted-foreground mt-3">
            Apenas o proprietário ou administrador pode mudar o plano.
          </p>
        )}
      </div>
      )}
      <PerguntaAntesDePagar aberta={!!pagamentoPendente} indicacao={indicacao} onCancelar={() => setPagamentoPendente(null)}
        onContinuar={() => { const p = pagamentoPendente; setPagamentoPendente(null); if (p) pagar(p.inv, p.metodo); }} />
    </div>
  );
}
